/**
 * CSV 取込み確認画面の集計サマリ計算。
 *
 * 入力:
 *   - parsed rows (CSV から)
 *   - existing entries (既存 BudgetRecord から SerializableBudgetEntry に変換済)
 *   - 自動作成科目名の一覧
 *
 * 出力 (CsvUploadSummary):
 *   - 予算行件数 / 実績行件数
 *   - 科目ごとの予算合計 / 実績積上合計 / 差異
 *   - 既存データへ上書きが発生する行の一覧
 *   - 自動作成される新規科目名
 */
import type {
  CsvCategoryAggregate,
  CsvOverwriteEntry,
  CsvUploadSummary,
} from "@ippon/shared-components";
import type { ParsedRow, SerializableBudgetEntry } from "./types";

const sumMonths = (m: Record<string, number | null>): number =>
  Object.values(m).reduce<number>((s, v) => s + (v ?? 0), 0);

export function buildCsvSummary(args: {
  rows: ParsedRow[];
  existingEntries: SerializableBudgetEntry[];
  newCategoryNames: string[];
  newExpenseCategoryItems?: CsvUploadSummary["newExpenseCategoryItems"];
}): CsvUploadSummary {
  const { rows, existingEntries, newCategoryNames, newExpenseCategoryItems } =
    args;

  // 件数
  let budgetRowCount = 0;
  let actualRowCount = 0;
  for (const r of rows) {
    if (r.rowKind === "予算") budgetRowCount++;
    else actualRowCount++;
  }

  // 科目別集計
  const aggMap = new Map<string, CsvCategoryAggregate>();
  for (const r of rows) {
    if (r.fiscalYear === "" || r.category === "") continue;
    const key = `${r.fiscalYear}|${r.group}|${r.category}`;
    let agg = aggMap.get(key);
    if (!agg) {
      agg = {
        fiscalYear: r.fiscalYear,
        category: r.category,
        group: r.group,
        budgetTotal: 0,
        actualTotal: 0,
        diff: 0,
      };
      aggMap.set(key, agg);
    }
    const sum = sumMonths(r.months);
    if (r.rowKind === "予算") agg.budgetTotal += sum;
    else agg.actualTotal += sum;
  }
  for (const a of aggMap.values()) a.diff = a.budgetTotal - a.actualTotal;
  // 並び順は CSV 内の出現順 (= ダウンロード CSV は予実表と同じ display_order 順で
  // 出力されるため、roundtrip 時にプレビューも予実表と一致する) を維持する。
  // 五十音順などの再ソートはかけない。
  const categoryAggregates = Array.from(aggMap.values());

  // 既存データとの上書き判定 — 値レベル diff
  //   - CSV の月セルが null (空欄) → "変更なし" 扱い、比較対象外
  //   - CSV の月セルが数値 → 既存値と比較。既存値が undefined/null なら 0 として比較
  //   - 1 つでも値が違えば上書きとして列挙
  // - 予算: 同 (年度, 大カテゴリ, 科目) の月別合算値 vs 既存 budgetMonths
  // - 実績: 同 (年度, 大カテゴリ, 科目) の row.months vs 既存 actualMonths (科目単位)
  // 既存データが (年度, カテゴリ名) で複数 BudgetRecord (category_id 違い) を持つ可能性があるため
  // budgetMonths / actualMonths とも月別に合算して一つにまとめてから比較する。
  const existingByCat = new Map<string, SerializableBudgetEntry>();
  for (const e of existingEntries) {
    const key = `${e.fiscalYear}|${e.group}|${e.category}`;
    const cur = existingByCat.get(key);
    if (!cur) {
      existingByCat.set(key, {
        ...e,
        budgetMonths: { ...e.budgetMonths },
        actualMonths: { ...e.actualMonths },
      });
      continue;
    }
    for (const [mm, v] of Object.entries(e.budgetMonths)) {
      if (v === null || v === undefined) continue;
      const prev = cur.budgetMonths[mm];
      cur.budgetMonths[mm] =
        (prev === null || prev === undefined ? 0 : prev) + v;
    }
    for (const [mm, v] of Object.entries(e.actualMonths)) {
      if (v === null || v === undefined) continue;
      const prev = cur.actualMonths[mm];
      cur.actualMonths[mm] =
        (prev === null || prev === undefined ? 0 : prev) + v;
    }
  }

  const valuesDiffer = (
    csvMonths: Record<string, number | null>,
    existingMonths: Record<string, number | null>,
  ): boolean => {
    for (const [mm, c] of Object.entries(csvMonths)) {
      if (c === null) continue;
      const e = existingMonths[mm] ?? 0;
      if (c !== e) return true;
    }
    return false;
  };
  const fmt = (n: number) => n.toLocaleString();

  // CSV 予算を (年度, 科目) ごとに月別合算
  const csvBudgetAgg = new Map<string, Record<string, number | null>>();
  for (const r of rows) {
    if (r.rowKind !== "予算") continue;
    if (!r.fiscalYear || !r.category) continue;
    const key = `${r.fiscalYear}|${r.group}|${r.category}`;
    if (!csvBudgetAgg.has(key)) csvBudgetAgg.set(key, {});
    const agg = csvBudgetAgg.get(key)!;
    for (const [mm, v] of Object.entries(r.months)) {
      if (v === null) continue;
      agg[mm] = ((agg[mm] as number | undefined) ?? 0) + v;
    }
  }

  const overwrites: CsvOverwriteEntry[] = [];
  // 予算の上書き判定
  for (const [key, csvMonths] of csvBudgetAgg) {
    const e = existingByCat.get(key);
    if (!e) continue;
    if (!valuesDiffer(csvMonths, e.budgetMonths)) continue;
    const [fiscalYear, , category] = key.split("|");
    const existingTotal = Object.values(e.budgetMonths).reduce<number>(
      (s, v) => s + (v ?? 0),
      0,
    );
    overwrites.push({
      fiscalYear,
      category,
      rowKind: "予算",
      existingSummary: `合計 ${fmt(existingTotal)} 円`,
    });
  }

  // 実績の上書き判定 — 科目単位 (詳細(区画)内訳は廃止)。
  // CSV 実績を (年度, 科目) ごとに月別合算してから既存 actualMonths と比較。
  const csvActualAgg = new Map<string, Record<string, number | null>>();
  for (const r of rows) {
    if (r.rowKind !== "実績") continue;
    if (!r.fiscalYear || !r.category) continue;
    const key = `${r.fiscalYear}|${r.group}|${r.category}`;
    if (!csvActualAgg.has(key)) csvActualAgg.set(key, {});
    const agg = csvActualAgg.get(key)!;
    for (const [mm, v] of Object.entries(r.months)) {
      if (v === null) continue;
      agg[mm] = ((agg[mm] as number | undefined) ?? 0) + v;
    }
  }
  for (const [key, csvMonths] of csvActualAgg) {
    const e = existingByCat.get(key);
    if (!e) continue;
    if (!valuesDiffer(csvMonths, e.actualMonths)) continue;
    const [fiscalYear, , category] = key.split("|");
    const sum = Object.values(e.actualMonths).reduce<number>(
      (s, v) => s + (v ?? 0),
      0,
    );
    overwrites.push({
      fiscalYear,
      category,
      rowKind: "実績",
      existingSummary: `合計 ${fmt(sum)} 円`,
    });
  }

  return {
    budgetRowCount,
    actualRowCount,
    categoryAggregates,
    overwrites,
    newCategoryNames,
    newExpenseCategoryItems,
  };
}
